import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WcOutlinedIcon from "@mui/icons-material/WcOutlined";
import BiotechOutlinedIcon from "@mui/icons-material/BiotechOutlined";
import FunctionsIcon from "@mui/icons-material/Functions";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import Badge from "@mui/material/Badge";
import Tooltip from "@mui/material/Tooltip";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  createReportTemplate,
  updateReportTemplate,
} from "api/reportTemplate";
import {
  REPORT_KINDS,
  FIELD_TYPES,
  DEFAULT_REPORT_KIND,
  DEFAULT_FIELD_TYPE,
  DROPDOWN_FIELD_TYPE,
  reportKindLabel,
} from "config/reportTemplateOptions";
import {
  getLabMethodOptions,
  getLabCalculationOptions,
} from "api/labMasters";
import {
  ReferenceRangesDialog,
  MethodsDialog,
  CalculationDialog,
  OptionsDialog,
} from "./ParameterConfigDialogs";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "78%",
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: "15px",
  boxShadow: 24,
  p: 4,
};

const emptyParameter = () => ({
  name: "",
  unit: "",
  normalRangeText: "",
  normalRangeMin: "",
  normalRangeMax: "",
  fieldType: DEFAULT_FIELD_TYPE,
  required: false,
  referenceRanges: [],
  methods: [],
  calculation: null,
  options: [],
});

const refId = (value) => {
  if (!value) return "";
  if (typeof value === "object") return value._id ? String(value._id) : "";
  return String(value);
};

const ConfigButton = ({ title, icon, count, onClick }) => (
  <Tooltip title={title}>
    <IconButton size="small" onClick={onClick} sx={{ color: "#4F46E5" }}>
      <Badge
        color="primary"
        badgeContent={count || 0}
        invisible={!count}
        sx={{ "& .MuiBadge-badge": { fontSize: 9, height: 14, minWidth: 14 } }}
      >
        {icon}
      </Badge>
    </IconButton>
  </Tooltip>
);

const normalizeName = (value) => (value || "").toString().trim().toLowerCase();

const EMPTY = "—";
const displayValue = (value) => {
  if (value === null || value === undefined) return EMPTY;
  const str = String(value).trim();
  return str === "" ? EMPTY : str;
};
const fieldTypeLabel = (value) =>
  FIELD_TYPES.find((f) => f.value === value)?.label || value || EMPTY;

const ReadField = ({ label, value, mono = false }) => (
  <Box>
    <Typography
      variant="caption"
      sx={{ color: "text.secondary", display: "block", mb: 0.5 }}
    >
      {label}
    </Typography>
    {mono ? (
      <Box
        component="span"
        sx={{
          fontFamily: "monospace",
          fontWeight: 600,
          bgcolor: "#F0F4FF",
          color: "#194AF5",
          px: 1.25,
          py: 0.5,
          borderRadius: "6px",
          display: "inline-block",
        }}
      >
        {value}
      </Box>
    ) : (
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    )}
  </Box>
);

const ReportTemplateFormModal = ({
  open,
  onClose,
  data,
  readOnly = false,
  investigationTypes = [],
  tests = [],
}) => {
  const isEdit = Boolean(data && data._id);

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [investigationType, setInvestigationType] = useState("");
  const [test, setTest] = useState("");
  const [reportKind, setReportKind] = useState(DEFAULT_REPORT_KIND);
  const [parameters, setParameters] = useState([emptyParameter()]);
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [labMethods, setLabMethods] = useState([]);
  const [labCalculations, setLabCalculations] = useState([]);
  const [configDialog, setConfigDialog] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    Promise.all([getLabMethodOptions(), getLabCalculationOptions()]).then(
      ([methods, calculations]) => {
        if (cancelled) return;
        setLabMethods(methods);
        setLabCalculations(calculations);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      setName(data.name || "");
      setCode(data.code || "");
      setInvestigationType(
        data.investigationType?._id || data.investigationType || ""
      );
      setTest(data.test?._id || data.test || "");
      setReportKind(data.reportKind || DEFAULT_REPORT_KIND);
      setParameters(
        Array.isArray(data.parameters) && data.parameters.length
          ? data.parameters.map((p) => ({
              name: p.name || "",
              unit: p.unit || "",
              normalRangeText: p.normalRangeText || "",
              normalRangeMin: p.normalRangeMin ?? "",
              normalRangeMax: p.normalRangeMax ?? "",
              fieldType: p.fieldType || DEFAULT_FIELD_TYPE,
              required: Boolean(p.required),
              referenceRanges: Array.isArray(p.referenceRanges)
                ? p.referenceRanges
                : [],
              methods: Array.isArray(p.methods) ? p.methods : [],
              calculation: p.calculation ?? null,
              options: Array.isArray(p.options) ? p.options : [],
            }))
          : [emptyParameter()]
      );
    }
  }, []);

  const duplicateRows = useMemo(() => {
    const seen = new Map();
    const dupes = new Set();
    parameters.forEach((p, idx) => {
      const key = normalizeName(p.name);
      if (!key) return;
      if (seen.has(key)) {
        dupes.add(idx);
        dupes.add(seen.get(key));
      } else {
        seen.set(key, idx);
      }
    });
    return dupes;
  }, [parameters]);

  const rangeErrorRows = useMemo(() => {
    const bad = new Set();
    parameters.forEach((p, idx) => {
      const min = p.normalRangeMin;
      const max = p.normalRangeMax;
      if (min !== "" && max !== "" && Number(min) > Number(max)) bad.add(idx);
    });
    return bad;
  }, [parameters]);

  const updateParameter = (index, field, value) => {
    setParameters((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const addParameter = () =>
    setParameters((prev) => [...prev, emptyParameter()]);

  const removeParameter = (index) =>
    setParameters((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== index) : prev
    );

  const openConfig = (index, type) => setConfigDialog({ index, type });
  const closeConfig = () => setConfigDialog(null);

  const commitConfig = (field) => (value) => {
    updateParameter(configDialog.index, field, value);
    closeConfig();
  };

  const configParameter = configDialog ? parameters[configDialog.index] : null;

  const handleSubmit = async () => {
    setSubmitted(true);

    if (!name.trim()) {
      toast.error("Template Name is required");
      return;
    }
    if (parameters.some((p) => !p.name.trim())) {
      toast.error("Each parameter must have a name");
      return;
    }
    if (duplicateRows.size > 0) {
      toast.error("Parameter names must be unique within a template");
      return;
    }
    if (rangeErrorRows.size > 0) {
      toast.error("Normal Range Min must be less than or equal to Max");
      return;
    }

    const payload = {
      name: name.trim(),
      ...(code.trim() && { code: code.trim() }),
      investigationType: investigationType || null,
      test: test || null,
      reportKind,
      parameters: parameters.map((p, idx) => ({
        name: p.name.trim(),
        unit: p.unit || "",
        normalRangeText: p.normalRangeText || "",
        normalRangeMin: p.normalRangeMin === "" ? null : Number(p.normalRangeMin),
        normalRangeMax: p.normalRangeMax === "" ? null : Number(p.normalRangeMax),
        fieldType: p.fieldType || DEFAULT_FIELD_TYPE,
        required: Boolean(p.required),
        displayOrder: idx,
        referenceRanges: Array.isArray(p.referenceRanges)
          ? p.referenceRanges
          : [],
        methods: (Array.isArray(p.methods) ? p.methods : [])
          .map((entry) => ({
            method: refId(entry?.method),
            isDefault: Boolean(entry?.isDefault),
          }))
          .filter((entry) => entry.method),
        calculation: refId(p.calculation) || null,
        options: Array.isArray(p.options) ? p.options : [],
      })),
    };

    setIsApiLoading(true);
    try {
      if (isEdit) {
        await updateReportTemplate(data._id, payload);
        toast.success("Report Template updated successfully");
      } else {
        await createReportTemplate(payload);
        toast.success("Report Template created successfully");
      }
      setIsApiLoading(false);
      onClose();
    } catch (err) {
      setIsApiLoading(false);
      toast.error(err?.response?.data?.message || "Something went wrong!");
    }
  };

  const title = readOnly
    ? "View Report Template"
    : isEdit
    ? "Edit Report Template"
    : "Add Report Template";

  // Resolved display values for read-only mode.
  const invTypeName =
    investigationTypes.find((it) => it._id === investigationType)?.name ||
    data?.investigationType?.name ||
    EMPTY;
  const testName =
    tests.find((t) => t._id === test)?.name || data?.test?.name || EMPTY;
  const isNarrative = reportKind === "NARRATIVE";

  return (
    <Modal open={open} onClose={() => onClose("close")}>
      <Box sx={modalStyle}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          {title}
        </Typography>

        {/* Section 1 — Basic Information */}
        <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
          Basic Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {readOnly ? (
              <ReadField label="Template Name" value={displayValue(name)} />
            ) : (
              <TextField
                fullWidth
                size="small"
                label="Template Name"
                required
                value={name}
                error={submitted && !name.trim()}
                helperText={
                  submitted && !name.trim() ? "Template Name is required" : ""
                }
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {readOnly ? (
              <ReadField label="Code" value={displayValue(code)} mono />
            ) : (
              <TextField
                fullWidth
                size="small"
                label="Code"
                placeholder="Auto-generated if left blank"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            {readOnly ? (
              <ReadField
                label="Investigation Type"
                value={displayValue(invTypeName)}
              />
            ) : (
              <FormControl fullWidth size="small">
                <InputLabel>Investigation Type</InputLabel>
                <Select
                  value={investigationType}
                  label="Investigation Type"
                  onChange={(e) => setInvestigationType(e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {investigationTypes.map((it) => (
                    <MenuItem key={it._id} value={it._id}>
                      {it.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            {readOnly ? (
              <ReadField label="Test" value={displayValue(testName)} />
            ) : (
              <FormControl fullWidth size="small">
                <InputLabel>Test</InputLabel>
                <Select
                  value={test}
                  label="Test"
                  onChange={(e) => setTest(e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {tests.map((t) => (
                    <MenuItem key={t._id} value={t._id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            {readOnly ? (
              <ReadField
                label="Report Kind"
                value={reportKindLabel(reportKind)}
              />
            ) : (
              <FormControl fullWidth size="small">
                <InputLabel>Report Kind</InputLabel>
                <Select
                  value={reportKind}
                  label="Report Kind"
                  onChange={(e) => setReportKind(e.target.value)}
                >
                  {REPORT_KINDS.map((k) => (
                    <MenuItem key={k.value} value={k.value}>
                      {k.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Section 2 — Parameters */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="subtitle1" fontWeight="600">
            Parameters
          </Typography>
          {!readOnly && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={addParameter}
              sx={{ borderColor: "#194AF5", color: "#194AF5" }}
            >
              Add Parameter
            </Button>
          )}
        </Box>

        {readOnly && isNarrative ? (
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              bgcolor: "#F9FAFB",
              borderRadius: "8px",
              border: "1px dashed #E0E0E0",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              This template does not contain structured parameters.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead sx={{ background: "#F0F4FF" }}>
                <TableRow>
                  {[
                    "Name",
                    "Unit",
                    "Normal Range Text",
                    "Min",
                    "Max",
                    "Field Type",
                    "Required",
                    "Configure",
                    ...(readOnly ? [] : [""]),
                  ].map((h, i) => (
                    <TableCell key={i} sx={{ fontWeight: "bold", fontSize: 13 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {parameters.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={readOnly ? { "& > td": { py: 1.5 } } : undefined}
                  >
                    <TableCell sx={{ minWidth: 160 }}>
                      {readOnly ? (
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {displayValue(row.name)}
                        </Typography>
                      ) : (
                        <TextField
                          fullWidth
                          size="small"
                          value={row.name}
                          error={
                            duplicateRows.has(index) ||
                            (submitted && !row.name.trim())
                          }
                          helperText={
                            duplicateRows.has(index)
                              ? "Duplicate name"
                              : submitted && !row.name.trim()
                              ? "Required"
                              : ""
                          }
                          onChange={(e) =>
                            updateParameter(index, "name", e.target.value)
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 90 }}>
                      {readOnly ? (
                        <Typography variant="body2">
                          {displayValue(row.unit)}
                        </Typography>
                      ) : (
                        <TextField
                          fullWidth
                          size="small"
                          value={row.unit}
                          onChange={(e) =>
                            updateParameter(index, "unit", e.target.value)
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      {readOnly ? (
                        <Typography variant="body2">
                          {displayValue(row.normalRangeText)}
                        </Typography>
                      ) : (
                        <TextField
                          fullWidth
                          size="small"
                          value={row.normalRangeText}
                          onChange={(e) =>
                            updateParameter(
                              index,
                              "normalRangeText",
                              e.target.value
                            )
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 90 }}>
                      {readOnly ? (
                        <Typography variant="body2">
                          {displayValue(row.normalRangeMin)}
                        </Typography>
                      ) : (
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={row.normalRangeMin}
                          error={rangeErrorRows.has(index)}
                          onChange={(e) =>
                            updateParameter(
                              index,
                              "normalRangeMin",
                              e.target.value
                            )
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 90 }}>
                      {readOnly ? (
                        <Typography variant="body2">
                          {displayValue(row.normalRangeMax)}
                        </Typography>
                      ) : (
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={row.normalRangeMax}
                          error={rangeErrorRows.has(index)}
                          helperText={
                            rangeErrorRows.has(index) ? "Min > Max" : ""
                          }
                          onChange={(e) =>
                            updateParameter(
                              index,
                              "normalRangeMax",
                              e.target.value
                            )
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      {readOnly ? (
                        <Typography variant="body2">
                          {fieldTypeLabel(row.fieldType)}
                        </Typography>
                      ) : (
                        <FormControl fullWidth size="small">
                          <Select
                            value={row.fieldType}
                            onChange={(e) =>
                              updateParameter(index, "fieldType", e.target.value)
                            }
                          >
                            {FIELD_TYPES.map((ft) => (
                              <MenuItem key={ft.value} value={ft.value}>
                                {ft.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </TableCell>
                    <TableCell align={readOnly ? "left" : "center"}>
                      {readOnly ? (
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {row.required ? "Yes" : "No"}
                        </Typography>
                      ) : (
                        <Checkbox
                          checked={row.required}
                          onChange={(e) =>
                            updateParameter(index, "required", e.target.checked)
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ minWidth: 140 }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.25,
                          justifyContent: "center",
                        }}
                      >
                        <ConfigButton
                          title="Configure Reference Ranges"
                          icon={<WcOutlinedIcon fontSize="small" />}
                          count={row.referenceRanges?.length}
                          onClick={() => openConfig(index, "ranges")}
                        />
                        <ConfigButton
                          title="Configure Methods"
                          icon={<BiotechOutlinedIcon fontSize="small" />}
                          count={row.methods?.length}
                          onClick={() => openConfig(index, "methods")}
                        />
                        <ConfigButton
                          title="Configure Calculation"
                          icon={<FunctionsIcon fontSize="small" />}
                          count={row.calculation ? 1 : 0}
                          onClick={() => openConfig(index, "calculation")}
                        />
                        {row.fieldType === DROPDOWN_FIELD_TYPE && (
                          <ConfigButton
                            title="Configure Options"
                            icon={<ListAltOutlinedIcon fontSize="small" />}
                            count={row.options?.length}
                            onClick={() => openConfig(index, "options")}
                          />
                        )}
                      </Box>
                    </TableCell>

                    {!readOnly && (
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          disabled={parameters.length === 1}
                          onClick={() => removeParameter(index)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Footer */}
        <Grid item display="flex" justifyContent="right" sx={{ mt: 4 }}>
          <Button
            variant="contained"
            onClick={() => onClose("close")}
            sx={{
              marginRight: "15px",
              bgcolor: "white",
              border: "1px solid #194AF5",
              color: "#194AF5",
              padding: "0.25rem 1.5rem",
              boxShadow: "none",
              ":hover": {
                bgcolor: "white",
                border: "1px solid #194AF5",
                boxShadow: "none",
              },
            }}
          >
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {!readOnly && (
            <Button
              variant="contained"
              disabled={isApiLoading}
              onClick={handleSubmit}
              sx={{ padding: "0.25rem 1.5rem", boxShadow: "none" }}
            >
              {isApiLoading ? "Saving..." : "Save"}
            </Button>
          )}
        </Grid>

        <ReferenceRangesDialog
          open={configDialog?.type === "ranges"}
          parameter={configParameter}
          readOnly={readOnly}
          onCancel={closeConfig}
          onSave={commitConfig("referenceRanges")}
        />
        <MethodsDialog
          open={configDialog?.type === "methods"}
          parameter={configParameter}
          methods={labMethods}
          readOnly={readOnly}
          onCancel={closeConfig}
          onSave={commitConfig("methods")}
        />
        <CalculationDialog
          open={configDialog?.type === "calculation"}
          parameter={configParameter}
          calculations={labCalculations}
          readOnly={readOnly}
          onCancel={closeConfig}
          onSave={commitConfig("calculation")}
        />
        <OptionsDialog
          open={configDialog?.type === "options"}
          parameter={configParameter}
          readOnly={readOnly}
          onCancel={closeConfig}
          onSave={commitConfig("options")}
        />
      </Box>
    </Modal>
  );
};

export default ReportTemplateFormModal;
